import { MANUFACTURER_IDS } from "./manufacturers.js";
import { MAX_PACK_LEVEL, packTemplate, packUnitLevel, type PackTemplate } from "./packs.js";
import { EXPEDITION_STORY_ART, type StoryPanelId, type StoryStageId } from "./expedition-story-art.js";

export const EXPEDITION_CHAPTERS = 12;
export const EXPEDITION_STAGES_PER_CHAPTER = 12;
export const EXPEDITION_STAMINA_CAP = 100;

export interface RewardBundle {
  resources?: Partial<Record<"RAT" | "POL" | "ALY" | "ENC" | "SCR", number>>;
  items?: { itemId: string; qty: number }[];
  credits?: number;
}

export interface ExpeditionStage {
  id: string;
  chapter: number;
  stage: number;
  boss: boolean;
  stamina: number;
  packLevel: number;
  enemy: PackTemplate;
  firstClear: RewardBundle;
  repeat: RewardBundle;
  /** Story beat shown before the fight (boss stages carry the chapter beats). */
  story: { title: { de: string; en: string }; lines: { de: string; en: string }; panelId: StoryPanelId } | null;
}

export interface ChapterChest {
  chapter: number;
  index: 1 | 2 | 3;
  stars: number;
  reward: RewardBundle;
}

const CHAPTER_TITLES: [string, string][] = [
  ["Der Zaun", "The Fence"],
  ["Ascheland", "Ashlands"],
  ["Die Bahnhofsruine", "The Station Ruin"],
  ["Rostgürtel", "Rust Belt"],
  ["Glasfelder", "Glass Fields"],
  ["Die Zisterne", "The Cistern"],
  ["Frostkorridor", "Frost Corridor"],
  ["Nekropole", "Necropolis"],
  ["Der Kanal", "The Canal"],
  ["Sporenwald", "Spore Forest"],
  ["Die Sperrzone", "The Exclusion Zone"],
  ["Ground Zero", "Ground Zero"],
];

/** Two beats per chapter: the stage-6 mid-boss and the stage-12 finale. */
const BEATS: [string, string, string, string][] = [
  ["Der Zaun hielt dreißig Jahre. Heute Nacht nicht mehr. Der Trupp rückt aus, um zu sehen, was ihn durchbrochen hat.", "The fence held for thirty years. Not tonight. The squad moves out to see what came through.",
   "Ein Koloss blockiert den Pass. Die Sensoren zählen mehr Herzschläge, als Körper zu sehen sind.", "A hulk blocks the pass. The sensors count more heartbeats than there are bodies to see."],
  ["Ascheregen. Die Rationen der alten Farm liegen noch da, bewacht von denen, die sie einst pflanzten.", "Ash rain. The old farm's rations are still there, guarded by the ones who once planted them.",
   "Etwas hat den Pass frei gefressen. Der Trupp folgt der Spur aus Knochen.", "Something ate the pass clear. The squad follows the trail of bones."],
  ["Die Bahnhofsruine sendet noch. Ein Signal auf der Notfrequenz, seit dreißig Jahren dasselbe Wort.", "The station ruin still transmits. One word on the emergency band, unchanged for thirty years.",
   "Der Sender war ein Köder. Unter dem Gleisbett brütet etwas Großes.", "The transmitter was bait. Something large nests beneath the track bed."],
  ["Rostgürtel: Redline-Schrottplätze, so weit die Optik reicht. Hier wurden Waffen gebaut.", "Rust Belt: Redline scrapyards as far as the optics reach. Weapons were built here.",
   "Hier wird noch gebaut. Die Pressen laufen, und niemand bedient sie.", "Something still builds. The presses run, and nobody works them."],
  ["Die Glasfelder singen im Wind. Jede Scherbe ein Infizierter, der stehen blieb, als der Himmel brannte.", "The glass fields sing in the wind. Every shard an infected that stopped when the sky burned.",
   "Zwischen den Scherben bewegt sich etwas mit zwei Oberkörpern.", "Between the shards something moves with two torsos."],
  ["Unter der Stadt sammelt die Zisterne, was oben stirbt. Der Trupp geht hinunter.", "Beneath the city the cistern collects what dies above. The squad goes down.",
   "Das Wasser ist warm. Es atmet.", "The water is warm. It breathes."],
  ["Der Frostkorridor konserviert alles. Auch den Hunger.", "The frost corridor preserves everything. Hunger included.",
   "Im Eis: ein Konvoi, drei Jahrzehnte alt, und seine Besatzung steht auf.", "In the ice: a convoy three decades old, and its crew stands up."],
  ["Die Nekropole war ein Krankenhaus. Die Patienten sind noch da.", "The necropolis was a hospital. The patients are still here.",
   "Auf der Intensivstation wartet der erste Nekromant.", "In intensive care waits the first necromancer."],
  ["Der Kanal führt nach Ground Zero. Die Schleusen sind offen, die Wächter nicht menschlich.", "The canal leads to Ground Zero. The locks stand open; the guards are not human.",
   "Eine Abscheulichkeit hält die letzte Schleuse. Sie hat gelernt, zu warten.", "An abomination holds the last lock. It has learned to wait."],
  ["Sporenwald. Die Träger heilen alles, was ihnen nahekommt. Feuer frei auf die Träger.", "Spore forest. The carriers heal everything near them. Fire on the carriers first.",
   "Der Wald hat ein Herz. Es schlägt unter dem größten Baum.", "The forest has a heart. It beats beneath the largest tree."],
  ["Die Sperrzone gehörte der Armee. Jetzt gehört sie dem Strain.", "The exclusion zone belonged to the army. Now it belongs to the strain.",
   "Ground Zero liegt dahinter. Der letzte Checkpoint wird von etwas gehalten, das Panzer bog.", "Ground Zero lies beyond. The last checkpoint is held by something that bent tanks."],
  ["Ground Zero. Der Krater, in dem alles begann. Das Bewusstsein des Strains kennt deinen Namen.", "Ground Zero. The crater where it all began. The strain's consciousness knows your name.",
   "Die Matriarchin des Kraters. Fällt sie, schweigt der Strain. Für eine Weile.", "The crater's Matriarch. When she falls, the strain goes silent. For a while."],
];

function stageIndex(chapter: number, stage: number): number {
  return (chapter - 1) * EXPEDITION_STAGES_PER_CHAPTER + (stage - 1);
}

/** Pack level rises linearly from 1 to 40 over the 144 stages. */
export function stagePackLevel(chapter: number, stage: number): number {
  const total = EXPEDITION_CHAPTERS * EXPEDITION_STAGES_PER_CHAPTER - 1;
  return 1 + Math.floor((stageIndex(chapter, stage) * (MAX_PACK_LEVEL - 1)) / total);
}

function bossEnemy(chapter: number, stage: number, packLevel: number): PackTemplate {
  const base = packTemplate(packLevel);
  const L = packUnitLevel(packLevel);
  const fighters = base.members.filter((m) => m.slot === "fighter").slice(0, 3);
  const supports = base.members.filter((m) => m.slot === "support");
  let bossId = "zmb.hulk";
  if (chapter === 8 && stage === EXPEDITION_STAGES_PER_CHAPTER) bossId = "zmb.necromancer";
  else if (chapter === EXPEDITION_CHAPTERS && stage === EXPEDITION_STAGES_PER_CHAPTER) bossId = "zmb.boss_hive_matriarch";
  else if (chapter >= 5 && stage === EXPEDITION_STAGES_PER_CHAPTER) bossId = "zmb.abomination";
  const commander = bossId === "zmb.boss_hive_matriarch" || bossId === "zmb.necromancer";
  const members = [{ zombieId: bossId, level: L + 3, slot: commander ? ("commander" as const) : ("fighter" as const) }, ...fighters, ...supports];
  return { level: packLevel, kind: "pack", members, power: Math.round(base.power * 1.6) };
}

function blueprintFor(chapter: number, stage: number): string {
  const mfr = MANUFACTURER_IDS[(chapter + stage / 3 - 1) % MANUFACTURER_IDS.length]!;
  const slot = ["weapon", "armor", "core", "sensor"][(stage / 3 - 1) % 4]!;
  const tier = chapter <= 4 ? "b" : chapter <= 8 ? "a" : "s";
  return `itm.blueprint:mod.${mfr.toLowerCase()}_${slot}_${tier}`;
}

function firstClear(chapter: number, stage: number, boss: boolean): RewardBundle {
  const scale = chapter * 3 + stage;
  const items: { itemId: string; qty: number }[] = [{ itemId: chapter <= 3 ? "itm.xp_500" : chapter <= 8 ? "itm.xp_2000" : "itm.xp_10000", qty: 1 + Math.floor(stage / 6) }];
  if (boss) items.push({ itemId: "itm.cdc", qty: 2 * chapter });
  if (stage % 3 === 0) items.push({ itemId: blueprintFor(chapter, stage), qty: 1 });
  if (stage === EXPEDITION_STAGES_PER_CHAPTER) items.push({ itemId: "itm.fab", qty: 1 });
  if (stage === 6) items.push({ itemId: "itm.nano", qty: 2 * chapter });
  return { resources: { RAT: 400 * scale, POL: 380 * scale, ALY: chapter >= 2 ? 120 * scale : 0, SCR: 25 * scale }, items, credits: boss ? 5 : 0 };
}

function repeat(chapter: number, stage: number, boss: boolean): RewardBundle {
  const scale = chapter * 2 + Math.ceil(stage / 2);
  const items: { itemId: string; qty: number }[] = [{ itemId: "itm.xp_500", qty: 1 }];
  if (boss) items.push({ itemId: "itm.cdc", qty: 1 });
  if (stage % 3 === 0) items.push({ itemId: "itm.nano", qty: 1 });
  return { resources: { RAT: 150 * scale, POL: 140 * scale, SCR: 8 * scale }, items };
}

/** 144 generated stages: monotonic pack level, bosses on 6 and 12, blueprints on 3/6/9/12 (40-combat.md R36). */
export const EXPEDITION_STAGES: readonly ExpeditionStage[] = Array.from({ length: EXPEDITION_CHAPTERS }, (_, ci) => ci + 1).flatMap((chapter) =>
  Array.from({ length: EXPEDITION_STAGES_PER_CHAPTER }, (_, si) => si + 1).map((stage): ExpeditionStage => {
    const boss = stage === 6 || stage === EXPEDITION_STAGES_PER_CHAPTER;
    const packLevel = stagePackLevel(chapter, stage);
    const beat = BEATS[chapter - 1]!;
    const lines = stage === 6 ? { de: beat[0], en: beat[1] } : { de: beat[2], en: beat[3] };
    return {
      id: `exp.${chapter}.${stage}`,
      chapter,
      stage,
      boss,
      stamina: 6 + chapter,
      packLevel,
      enemy: boss ? bossEnemy(chapter, stage, packLevel) : packTemplate(packLevel),
      firstClear: firstClear(chapter, stage, boss),
      repeat: repeat(chapter, stage, boss),
      story: boss ? { title: { de: CHAPTER_TITLES[chapter - 1]![0], en: CHAPTER_TITLES[chapter - 1]![1] }, lines, panelId: EXPEDITION_STORY_ART[`exp.${chapter}.${stage}` as StoryStageId].id } : null,
    };
  }),
);

const stageMap = new Map(EXPEDITION_STAGES.map((s) => [s.id, s]));

export function expeditionStageById(id: string): ExpeditionStage {
  const s = stageMap.get(id);
  if (!s) throw new Error(`unknown expedition stage ${id}`);
  return s;
}

export function chapterTitle(chapter: number): { de: string; en: string } {
  const t = CHAPTER_TITLES[chapter - 1];
  if (!t) throw new RangeError(`no chapter ${chapter}`);
  return { de: t[0], en: t[1] };
}

/** Three chests per chapter at 12 / 24 / 36 stars. */
export const CHAPTER_CHESTS: readonly ChapterChest[] = Array.from({ length: EXPEDITION_CHAPTERS }, (_, ci) => ci + 1).flatMap((chapter) =>
  ([1, 2, 3] as const).map((index): ChapterChest => ({
    chapter,
    index,
    stars: index * 12,
    reward: {
      resources: { RAT: 5_000 * chapter * index, POL: 5_000 * chapter * index, ALY: 1_500 * chapter * index },
      items: [
        { itemId: "itm.cdc", qty: 3 * chapter * index },
        { itemId: index === 3 ? "itm.ffab" : "itm.fab", qty: index },
        { itemId: `itm.ufr_${chapter <= 4 ? "c" : chapter <= 8 ? "b" : "a"}`, qty: 10 * index },
      ],
      credits: 10 * index,
    },
  })),
);

/** Sweeping a three-star stage repeats its `repeat` rewards without a battle; costs stamina and a ticket unless free. */
export const SWEEP_FREE_PER_DAY = 3;
export const SWEEP_FREE_OFFICE_LEVEL = 5;
